import { Injectable } from '@angular/core';
import { WebApiClient } from '../web-api-client.service';
import { WebApiService } from '../web-api.service';

/** 組合商品子項目定義 */
export interface CombineDetailCacheDto {
  /** 子商品識別碼 */
  ProductId: number;
  /** 子商品貨號 */
  ProductCode: string;
  /** 子商品名稱 */
  ProductName: string;
  /** 組成比例 (組合中該子商品的數量) */
  CompositionQty: number;
  /** 庫存類別 (1:一般商品, 2:母商品) */
  StockType: number;
  /** 是否使用批號 (商品標記用) */
  IsUseBatchNo?: boolean;
  /** 是否計算庫存 (商品標記用) */
  IsInventory?: boolean;
}

/** 組合商品子項目 (含需求數量計算) */
export interface CombineDetailWithQtyDto {
  /** 子商品識別碼 */
  ProductId: number;
  /** 子商品貨號 */
  ProductCode: string;
  /** 子商品名稱 */
  ProductName: string;
  /** 組成比例 (組合中該子商品的數量) */
  CompositionQty: number;
  /** 需求數量 (父商品數量 × 組成比例) */
  RequiredQty: number;
  /** 庫存類別 (1:一般商品, 2:母商品) */
  StockType: number;
  /** 是否使用批號 (商品標記用) */
  IsUseBatchNo?: boolean;
  /** 是否計算庫存 (商品標記用) */
  IsInventory?: boolean;
}

/** 組合商品快取服務 */
@Injectable({ providedIn: 'root' })
export class ProductCombineCacheService {
  /** 快取：ProductId (組合商品) → 子商品列表定義 */
  private cache: Map<number, CombineDetailCacheDto[]> = new Map();
  /** 載入中的 Promise */
  private loadingMap: Map<number, Promise<CombineDetailCacheDto[]>> = new Map();

  /** API Service */
  private api: WebApiService;

  constructor(private webApiClient: WebApiClient) {
    this.api = this.webApiClient.createHisService('stocks/ProductCombine');
  }

  /**
   * 取得組合商品的子項目定義
   * @param productId 組合商品識別碼
   * @returns 子商品列表定義
   */
  async getCombineDetails(productId: number): Promise<CombineDetailCacheDto[]> {
    // 已快取
    if (this.cache.has(productId)) {
      return this.cache.get(productId);
    }

    // 正在載入中，等待完成
    if (this.loadingMap.has(productId)) {
      return this.loadingMap.get(productId);
    }

    // 快取未命中，使用批次 API 載入單筆
    const loadingPromise = this.api.get('GetCombineDetailsBatch', {
      productIds: productId.toString()
    }).toPromise()
      .then((data: { ProductId: number; Details: CombineDetailCacheDto[] }[]) => {
        const item = (data || []).find(d => d.ProductId === productId);
        const result = item?.Details || [];
        this.cache.set(productId, result);
        this.loadingMap.delete(productId);
        return result;
      })
      .catch(error => {
        console.error(`載入組合商品 ${productId} 子項目失敗`, error);
        this.loadingMap.delete(productId);
        return [];
      });

    this.loadingMap.set(productId, loadingPromise);
    return loadingPromise;
  }

  /**
   * 取得組合商品子項目 (含需求數量計算)
   * @param productId 組合商品識別碼
   * @param parentQty 父商品數量
   * @returns 子商品列表含需求數量
   */
  async getCombineDetailsWithQty(
    productId: number,
    parentQty: number = 1
  ): Promise<CombineDetailWithQtyDto[]> {
    // 先取得子項目定義
    const details = await this.getCombineDetails(productId);

    if (!details || details.length === 0) {
      return [];
    }

    // 計算需求數量
    return details.map(d => ({
      ProductId: d.ProductId,
      ProductCode: d.ProductCode,
      ProductName: d.ProductName,
      CompositionQty: d.CompositionQty,
      RequiredQty: d.CompositionQty * parentQty,
      StockType: d.StockType,
      IsUseBatchNo: d.IsUseBatchNo,
      IsInventory: d.IsInventory
    }));
  }

  /**
   * 預載多個組合商品的子項目定義
   * @param productIds 組合商品識別碼陣列
   */
  async preloadCombineDetails(productIds: number[]): Promise<void> {
    const notCachedIds = productIds.filter(id => !this.cache.has(id));
    if (notCachedIds.length === 0) {
      return;
    }

    // 批次載入
    try {
      const data = await this.api.get('GetCombineDetailsBatch', {
        productIds: notCachedIds.join(',')
      }).toPromise() as { ProductId: number; Details: CombineDetailCacheDto[] }[];

      (data || []).forEach(item => {
        this.cache.set(item.ProductId, item.Details || []);
      });
    } catch (error) {
      console.error('批次載入組合商品子項目失敗', error);
    }
  }

  /**
   * 清除快取
   * @param productId 若指定則只清除該商品，否則清除全部
   */
  clearCache(productId?: number): void {
    if (productId !== undefined) {
      this.cache.delete(productId);
      this.loadingMap.delete(productId);
    } else {
      this.cache.clear();
      this.loadingMap.clear();
    }
  }

  /**
   * 檢查特定商品是否已快取
   * @param productId 組合商品識別碼
   */
  isCached(productId: number): boolean {
    return this.cache.has(productId);
  }

  /**
   * 取得快取商品數量
   */
  getCacheCount(): number {
    return this.cache.size;
  }
}
